import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/lib/settings";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2d500651-6ee1-4528-b8ea-6b16a98350c3/artifacts/unpk7yag_image.png";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/schedule", label: "Class Times", testId: "nav-booking" },
  { to: "/pricing", label: "Pricing", testId: "nav-pricing" },
  { to: "/guides", label: "Text Guides", testId: "nav-guides" },
  { to: "/blog", label: "Blog", testId: "nav-blog" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
  { to: "/member/login", label: "Members", testId: "nav-members" },
];

export const Navbar = ({ isAdmin, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const settings = useSiteSettings();
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/90 dark:bg-[#0f1117]/90 border-b border-slate-100 dark:border-slate-800 transition-colors duration-300" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Tutors Melbourne" className="h-10 w-auto" />
          <div className="hidden sm:block">
            <span className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light leading-none">Tutors Melbourne</span>
            <span className="block text-xs text-brand-gold font-semibold tracking-wide">Learning Perfected</span>
          </div>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          {isAdmin ? (
            <>
              <Link to="/admin/dashboard" className="text-slate-600 dark:text-slate-300 hover:text-brand-purple dark:hover:text-brand-purple-light font-medium transition-colors text-sm" data-testid="nav-dashboard">Dashboard</Link>
              <Button onClick={onLogout} variant="outline" size="sm" className="rounded-full border-brand-purple text-brand-purple hover:bg-brand-purple-soft dark:border-brand-purple-light dark:text-brand-purple-light dark:hover:bg-brand-purple/20" data-testid="nav-logout-btn">
                Logout
              </Button>
            </>
          ) : (
            <>
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-medium transition-colors text-sm ${
                    isActive(link.to)
                      ? "text-brand-purple dark:text-brand-purple-light"
                      : "text-slate-600 dark:text-slate-300 hover:text-brand-purple dark:hover:text-brand-purple-light"
                  }`}
                  data-testid={link.testId}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/admin/login" className="text-slate-400 dark:text-slate-500 hover:text-brand-purple dark:hover:text-brand-purple-light font-medium transition-colors text-xs" data-testid="nav-admin-login">Admin</Link>
              <Link to="/enrol" data-testid="nav-enrol-cta">
                <Button size="sm" className="rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-semibold px-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  Enrol For {settings.term_label}
                </Button>
              </Link>
            </>
          )}
          <button onClick={() => setDark(!dark)} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="theme-toggle-btn" aria-label="Toggle theme">
            {dark ? <Sun className="h-4 w-4 text-brand-gold" /> : <Moon className="h-4 w-4 text-brand-purple" />}
          </button>
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-2">
          <button onClick={() => setDark(!dark)} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="theme-toggle-mobile" aria-label="Toggle theme">
            {dark ? <Sun className="h-4 w-4 text-brand-gold" /> : <Moon className="h-4 w-4 text-brand-purple" />}
          </button>
          <button className="p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" /> : <Menu className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white dark:bg-[#0f1117] border-t border-slate-100 dark:border-slate-800 px-6 py-4 space-y-3 animate-fade-in" data-testid="nav-mobile-menu">
          {isAdmin ? (
            <>
              <Link to="/admin/dashboard" className="block text-slate-600 dark:text-slate-300 hover:text-brand-purple font-medium py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <button onClick={() => { onLogout(); setMobileOpen(false); }} className="text-brand-purple dark:text-brand-purple-light font-medium py-2">Logout</button>
            </>
          ) : (
            <>
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block font-medium py-2 ${
                    isActive(link.to)
                      ? "text-brand-purple dark:text-brand-purple-light"
                      : "text-slate-600 dark:text-slate-300 hover:text-brand-purple"
                  }`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${link.testId}-mobile`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/admin/login" className="block text-slate-400 dark:text-slate-500 hover:text-brand-purple font-medium py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="nav-admin-login-mobile">Admin</Link>
              <Link to="/enrol" onClick={() => setMobileOpen(false)} className="block" data-testid="nav-enrol-cta-mobile">
                <Button className="w-full rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-semibold">
                  Enrol For {settings.term_label}
                </Button>
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};
